import { useState, useRef } from 'react';
import { getFiles, ingestFile, deleteFile, crawlUrl } from '../services/fileService';

export const useDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isLoadingMoreDocs, setIsLoadingMoreDocs] = useState(false);
  const [totalDocCount, setTotalDocCount] = useState(0);
  const [crawlUrlInput, setCrawlUrlInput] = useState('');
  const [crawlLoading, setCrawlLoading] = useState(false);
  const [crawlStatus, setCrawlStatus] = useState('');
  
  const pollingIntervalRef = useRef(null);
  const documentsEndRef = useRef(null);

  const loadDocuments = async (page = 1, isInitialLoad = true) => {
    try {
      if (isInitialLoad) {
        setUploadLoading(true);
        setDocuments([]);
        setCurrentPage(1);
        setHasMorePages(true);
      } else {
        setIsLoadingMoreDocs(true);
      }

      const response = await getFiles('main', page, 30);
      const files = response.data?.files || [];
      
      const totalCount = response.data?.total_count;
      if (totalCount !== undefined) {
        setTotalDocCount(totalCount);
      }
      
      if (files.length === 0) {
        setHasMorePages(false);
      } else {
        setCurrentPage(page);
        setDocuments(prev => isInitialLoad ? files : [...prev, ...files]);
      }
    
      const hasProcessingFiles = files.some(file => 
        file.status === 'preprocessing' || 
        file.status === 'indexing' || 
        file.status === 'processing'
      );
      
      if (hasProcessingFiles) {
        startPolling();
      } else {
        stopPolling();
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      setHasMorePages(false);
    } finally {
      setUploadLoading(false);
      setIsLoadingMoreDocs(false);
    }
  };

  const pollDocumentStatus = async () => {
    try {
      // Fetch documents silently without showing loading indicators
      const response = await getFiles('main', 1, 30);
      const files = response.data?.files || [];
      
      const totalCount = response.data?.total_count;
      if (totalCount !== undefined) {
        setTotalDocCount(totalCount);
      }
      
      // Update documents only if there are changes in status
      setDocuments(prevDocs => {
        // Create a map of new files by ID for quick lookup
        const newFilesMap = new Map(files.map(file => [file.id, file]));
        
        // Check if any status has changed
        const hasChanges = prevDocs.some(prevDoc => {
          const newDoc = newFilesMap.get(prevDoc.id);
          return newDoc && newDoc.status !== prevDoc.status;
        });
        
        // Only update if there are actual changes
        if (hasChanges || prevDocs.length !== files.length) {
          return files;
        }
        
        return prevDocs;
      });
      
      // Check if we should continue polling
      const hasProcessingFiles = files.some(file => 
        file.status === 'preprocessing' || 
        file.status === 'indexing' || 
        file.status === 'processing'
      );
      if (!hasProcessingFiles) {
        stopPolling();
      }
    } catch (error) {
      console.error('Error polling document status:', error);
    }
  };

  const handleFileUpload = async (files, setMessages) => {
    if (files.length === 0) return;

    setUploadLoading(true);
    try {
      for (const file of files) {
        try {
          const response = await ingestFile(file, 'main');
          
          if (response.status === 200 || response.status === 201) {
            setMessages(prev => [...prev, {
              text: `Document "${file.name}" upload started successfully. Check the Documents drawer for processing status.`,
              sender: 'ai'
            }]);
          } else {
            setMessages(prev => [...prev, {
              text: `Failed to upload "${file.name}". Please try again.`,
              sender: 'ai'
            }]);
          }
        } catch (fileError) {
          console.error(`Error uploading ${file.name}:`, fileError);
          setMessages(prev => [...prev, {
            text: `Failed to upload "${file.name}": ${fileError.message}`,
            sender: 'ai'
          }]);
        }
      }
      
      loadDocuments(1, true);
    } catch (error) {
      console.error('Error uploading files:', error);
      setMessages(prev => [...prev, {
        text: 'Sorry, there was an error uploading one or more documents.',
        sender: 'ai'
      }]);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteDocument = async (fileId) => {
    try {
      await deleteFile(fileId, 'main');
      loadDocuments(1, true);
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleCrawl = async (crawlOptions = {}) => {
    if (!crawlUrlInput.trim()) return;
    setCrawlLoading(true);
    setCrawlStatus('Starting crawl...');
    try {
      await crawlUrl(
        crawlUrlInput.trim(),
        'main',
        crawlOptions.maxPage || 100,
        crawlOptions.maxDepth || 3,
        crawlOptions.crawlSubDomain !== undefined ? crawlOptions.crawlSubDomain : true,
        crawlOptions.fetchFiles !== undefined ? crawlOptions.fetchFiles : false
      );
      setCrawlStatus('Crawl started successfully');
      setCrawlUrlInput('');
      loadDocuments(1, true);
    } catch (error) {
      setCrawlStatus('Crawl failed');
    } finally {
      setCrawlLoading(false);
    }
  };

  const startPolling = () => {
    if (!pollingIntervalRef.current) {
      pollingIntervalRef.current = setInterval(() => {
        pollDocumentStatus();
      }, 5000);
    }
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  return {
    documents,
    uploadLoading,
    currentPage,
    hasMorePages,
    isLoadingMoreDocs,
    totalDocCount,
    crawlUrlInput,
    setCrawlUrlInput,
    crawlLoading,
    crawlStatus,
    documentsEndRef,
    loadDocuments,
    handleFileUpload,
    handleDeleteDocument,
    handleCrawl,
    stopPolling
  };
};

